import { CheckCircle2, AlertCircle } from "lucide-react";

import { CONFIRMED_NOTE, useExtractionStore } from "./useExtractionStore";
import { cn } from "@/lib/utils";

interface ConfidenceStampProps {
  fieldName: string;
  className?: string;
}

/**
 * Tiny inline indicator next to a form input. Binary on purpose — the
 * architect's decision is "trust it or check it". F2: the green stamp
 * carries a readable WORD for where the value came from ("iz nacrta" /
 * "iz dokumenta" / "iz opisa"), the amber one says "provjeri"; the
 * tooltip explains the source, the confidence and what to do about it.
 * Q1: the amber stamp carries a "Potvrdi" action — the user vouches for
 * the value, the stamp flips to "potvrđeno" and the field leaves the
 * "za provjeru" set. Nothing renders for a hand-typed field.
 */
export function ConfidenceStamp({ fieldName, className }: ConfidenceStampProps) {
  const meta = useExtractionStore((s) => s.confidence[fieldName]);
  const confirmField = useExtractionStore((s) => s.confirmField);
  if (!meta || meta.value == null || meta.value === "") return null;
  const isConfirmed = meta.source?.note === CONFIRMED_NOTE;
  const isHigh = meta.confidence >= 0.75;
  const source = describeSource(meta.source?.extractor);

  if (isConfirmed) {
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1 text-[0.7rem] font-medium text-green-700",
          className,
        )}
        title={`${source.long} — potvrdio korisnik.`}
      >
        <CheckCircle2 className="h-3 w-3" />
        potvrđeno
      </span>
    );
  }

  const tooltip = [
    `${source.long} — povjerenje ${(meta.confidence * 100).toFixed(0)} %.`,
    isHigh
      ? "Ručni unos u polje briše oznaku."
      : "Provjeri vrijednost pa je potvrdi ili ispravi.",
    meta.source?.note ?? "",
  ]
    .filter(Boolean)
    .join("\n");
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 text-[0.7rem] font-medium",
        isHigh ? "text-green-700" : "text-amber-700",
        className,
      )}
      title={tooltip}
    >
      {isHigh ? (
        <>
          <CheckCircle2 className="h-3 w-3" />
          {source.short}
        </>
      ) : (
        <>
          <AlertCircle className="h-3 w-3" />
          provjeri
          <button
            type="button"
            onClick={() => confirmField(fieldName)}
            className="ml-1 rounded border border-amber-300 bg-amber-100 px-1.5 py-px text-[0.68rem] font-semibold text-amber-800 hover:bg-amber-200"
            title="Vrijednost je točna — makni je iz popisa za provjeru."
          >
            Potvrdi
          </button>
        </>
      )}
    </span>
  );
}

/** Source labels: ``short`` renders next to the input (green stamps),
 * ``long`` goes to the tooltip. The backend stamps the extractor tag
 * (``nl_extractor``, ``building``, ``plot``, …) on each ``FieldCandidate``;
 * we map the NL and document ones explicitly and treat everything else as
 * "iz nacrta" so DXF/IFC/situacija extractor names stay an implementation
 * detail. */
function describeSource(extractor: string | undefined): {
  short: string;
  long: string;
} {
  if (extractor === "nl_extractor") {
    return { short: "iz opisa", long: "Pročitano iz opisa (glas/tekst/dokument)" };
  }
  if (extractor === "document_recognizer") {
    return { short: "iz dokumenta", long: "Pročitano iz prepoznatog dokumenta" };
  }
  return { short: "iz nacrta", long: "Izmjereno iz nacrta (CAD/situacija)" };
}
